import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';

export const useTickets = (params, options = {}) => {
  const currentUser = useAppStore((state) => state.currentUser);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const effectiveUserId = viewAsUser?.id || currentUser?.id;

  return useQuery({
    queryKey: ['tickets', effectiveUserId, params],
    queryFn: async () => {
      const { data } = await ticketsApi.list(params);
      return data;
    },
    ...options,
  });
};

export const useTicket = (id) => {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => {
      const { data } = await ticketsApi.get(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useTicketEvents = (id) => {
  return useQuery({
    queryKey: ['ticketEvents', id],
    queryFn: async () => {
      const { data } = await ticketsApi.getEvents(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ticketData) => ticketsApi.create(ticketData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
};

export const useApproveTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => ticketsApi.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });
};

export const useRejectTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reason }) => ticketsApi.reject(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });
};

export const useAssignTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, techId }) => ticketsApi.assign(id, techId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });
};